import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useSuiteStore } from './useSuiteStore'
import { useAuthStore } from './useAuthStore'

export type RequestSource = 'TICKET' | 'EMAIL' | 'CHAT' | 'TELEPHONE' | 'DIRECT' | 'AUTRE'

export type RequestStatus =
  | 'BROUILLON'
  | 'A_CADRER'
  | 'A_CHIFFRER'
  | 'CHIFFRE'
  | 'PRIORISE'
  | 'PRET_A_PLANIFIER'
  | 'A_FAIRE'
  | 'EN_COURS'
  | 'RECETTE_INTERNE'
  | 'RECETTE_METIER'
  | 'PRET_A_DEPLOYER'
  | 'LIVREE'
  | 'COMMUNICATION_FAITE'
  | 'ANNULEE'

interface PoleDevRequester {
  userUuid: string
  firstName?: string
  lastName?: string
  email?: string
}

interface PoleDevComment {
  userUuid: string
  firstName?: string
  lastName?: string
  message: string
  createdAt: string
}

interface PoleDevStatusEntry {
  status: RequestStatus
  userUuid: string
  firstName?: string
  lastName?: string
  at: string
  note?: string
}

export interface PoleDevTask {
  id: string
  title: string
  description?: string
  estimatedHours?: number
}

interface PoleDevReformulation {
  text: string
  tasks: PoleDevTask[]
}

export interface PoleDevRequest {
  _id: string
  code: string
  title: string
  description: string
  source: RequestSource
  requester: PoleDevRequester
  requestedAt?: string
  reformulation: PoleDevReformulation
  metadata: Record<string, unknown>
  comments: PoleDevComment[]
  status: RequestStatus
  statusHistory: PoleDevStatusEntry[]
  estimatedHours?: number
  githubIssueUrl?: string
  githubProjectItemId?: string
  githubIssueNumber?: number
  createdAt: string
  updatedAt: string
}

interface SprintUserCardStat {
  title: string
  estimate: number
  status: string
  url: string | null
  number: number | null
}

interface SprintUserStat {
  login: string
  cardCount: number
  totalEstimate: number
  completionRate: number
  statusCounts: Record<string, number>
  cards: SprintUserCardStat[]
}

export interface SprintStats {
  sprintTitle: string
  startDate: string
  duration: number
  totalCards: number
  totalEstimate: number
  completedCards: number
  completedEstimate: number
  completionRate: number
  users: SprintUserStat[]
  statusCounts: Record<string, number>
}

export interface PoleDevFilters {
  status?: RequestStatus[]
  search?: string
  requesterUuid?: string
  from?: string
  to?: string
  page?: number
  pageSize?: number
}

export const usePoleDevStore = defineStore('poleDev', () => {
  const suiteStore = useSuiteStore()
  const authStore = useAuthStore()

  const requests = ref<PoleDevRequest[]>([])
  const total = ref(0)
  const page = ref(1)
  const pageSize = ref(50)
  const request = ref<PoleDevRequest | null>(null)
  const loading = ref(false)

  function buildQuery(filters: PoleDevFilters): string {
    const params = new URLSearchParams()
    if (filters.status && filters.status.length > 0) filters.status.forEach((s) => params.append('status', s))
    if (filters.search) params.append('search', filters.search)
    if (filters.requesterUuid) params.append('requesterUuid', filters.requesterUuid)
    if (filters.from) params.append('from', filters.from)
    if (filters.to) params.append('to', filters.to)
    if (filters.page) params.append('page', String(filters.page))
    if (filters.pageSize) params.append('pageSize', String(filters.pageSize))
    return params.toString()
  }

  async function fetchAll(filters: PoleDevFilters = {}) {
    loading.value = true
    try {
      const qs = buildQuery({ page: 1, pageSize: 50, ...filters })
      const res = await suiteStore.suiteFetch(`/pole-dev/requests${qs ? `?${qs}` : ''}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      })
      if (!res || !res.ok) throw new Error('Echec de la récupération des demandes')
      const data = await res.json()
      requests.value = data.items ?? []
      total.value = data.total ?? 0
      page.value = data.page ?? 1
      pageSize.value = data.pageSize ?? 50
      return data
    } finally {
      loading.value = false
    }
  }

  async function fetchOne(id: string) {
    loading.value = true
    try {
      const res = await suiteStore.suiteFetch(`/pole-dev/requests/${id}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      })
      if (!res || !res.ok) throw new Error('Echec de la récupération de la demande')
      request.value = await res.json()
      return request.value
    } finally {
      loading.value = false
    }
  }

  async function create(body: {
    title: string
    description?: string
    source: RequestSource
    requesterUuid: string
    requestedAt?: string
  }) {
    loading.value = true
    try {
      const res = await suiteStore.suiteFetch('/pole-dev/requests', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-csrf-token': authStore.getCoreCsrfToken(),
        },
        credentials: 'include',
        body: JSON.stringify(body),
      })
      if (!res || !res.ok) throw new Error('Echec de la création')
      return (await res.json()) as PoleDevRequest
    } finally {
      loading.value = false
    }
  }

  async function update(
    id: string,
    patch: Partial<Pick<PoleDevRequest, 'title' | 'description' | 'reformulation' | 'estimatedHours' | 'githubIssueUrl' | 'source' | 'requestedAt'>> & {
      requesterUuid?: string
    },
  ) {
    loading.value = true
    try {
      const res = await suiteStore.suiteFetch(`/pole-dev/requests/${id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'x-csrf-token': authStore.getCoreCsrfToken(),
        },
        credentials: 'include',
        body: JSON.stringify(patch),
      })
      if (!res || !res.ok) throw new Error('Echec de la mise à jour')
      await fetchOne(id)
    } finally {
      loading.value = false
    }
  }

  async function addComment(id: string, message: string) {
    loading.value = true
    try {
      const res = await suiteStore.suiteFetch(`/pole-dev/requests/${id}/comments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-csrf-token': authStore.getCoreCsrfToken(),
        },
        credentials: 'include',
        body: JSON.stringify({ message }),
      })
      if (!res || !res.ok) throw new Error("Echec de l'ajout du commentaire")
      await fetchOne(id)
    } finally {
      loading.value = false
    }
  }

  async function changeStatus(id: string, status: RequestStatus, note?: string) {
    loading.value = true
    try {
      const res = await suiteStore.suiteFetch(`/pole-dev/requests/${id}/status`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-csrf-token': authStore.getCoreCsrfToken(),
        },
        credentials: 'include',
        body: JSON.stringify({ status, note }),
      })
      if (!res || !res.ok) throw new Error('Echec du changement de statut')
      await fetchOne(id)
    } finally {
      loading.value = false
    }
  }

  async function syncGithub(id: string) {
    loading.value = true
    try {
      const res = await suiteStore.suiteFetch(`/pole-dev/requests/${id}/sync-github`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-csrf-token': authStore.getCoreCsrfToken(),
        },
        credentials: 'include',
      })
      if (!res || !res.ok) {
        const err = res && typeof res !== 'boolean' ? await res.json().catch(() => ({} as { message?: string })) : {}
        throw new Error((err as { message?: string })?.message ?? 'Echec du sync GitHub')
      }
      await fetchOne(id)
      return (await res.json()) as { syncedStatus: boolean; syncedEstimate: boolean }
    } finally {
      loading.value = false
    }
  }

  async function pushToGithub(id: string) {
    loading.value = true
    try {
      const res = await suiteStore.suiteFetch(`/pole-dev/requests/${id}/push-to-github`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-csrf-token': authStore.getCoreCsrfToken(),
        },
        credentials: 'include',
      })
      if (!res || !res.ok) {
        const err = res && typeof res !== 'boolean' ? await res.json().catch(() => ({} as { message?: string })) : {}
        throw new Error((err as { message?: string })?.message ?? 'Echec du push GitHub')
      }
      const data = (await res.json()) as { request: PoleDevRequest; alreadyExisted: boolean }
      // Refresh local
      await fetchOne(id)
      return data
    } finally {
      loading.value = false
    }
  }

  async function fetchSprintStats(query: { from: string; to: string; completedStatuses?: string[] }) {
    const params = new URLSearchParams()
    params.append('from', query.from)
    params.append('to', query.to)
    if (query.completedStatuses) query.completedStatuses.forEach((s) => params.append('completedStatuses', s))
    const res = await suiteStore.suiteFetch(`/pole-dev/requests/github/sprint-stats?${params.toString()}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    })
    if (!res || !res.ok) throw new Error('Echec de la récupération des stats sprint')
    return (await res.json()) as SprintStats[]
  }

  async function fetchReadyForCommunication() {
    loading.value = true
    try {
      const res = await suiteStore.suiteFetch('/pole-dev/requests/communications/ready', {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      })
      if (!res || !res.ok) throw new Error('Echec de la récupération des demandes à communiquer')
      return (await res.json()) as PoleDevRequest[]
    } finally {
      loading.value = false
    }
  }

  async function sendCommunication(payload: {
    requestIds: string[]
    recipients: { email: string; name?: string }[]
    subject?: string
    templateCode?: string
  }) {
    loading.value = true
    try {
      const res = await suiteStore.suiteFetch('/pole-dev/requests/communications', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-csrf-token': authStore.getCoreCsrfToken(),
        },
        credentials: 'include',
        body: JSON.stringify(payload),
      })
      if (!res || !res.ok) {
        const errBody = res && typeof res !== 'boolean' ? await res.json().catch(() => ({} as { message?: string })) : {}
        throw new Error((errBody as { message?: string })?.message ?? "Echec de l'envoi de la communication")
      }
      return (await res.json()) as {
        sent: string[]
        errors: { email: string; error: string }[]
        ignored: { code: string; status: string }[]
        processedRequestCodes: string[]
      }
    } finally {
      loading.value = false
    }
  }

  async function exportRequests(filters: PoleDevFilters & { format?: 'csv' | 'xlsx' } = {}) {
    const qs = buildQuery(filters)
    const params = qs ? `?${qs}&format=${filters.format ?? 'xlsx'}` : `?format=${filters.format ?? 'xlsx'}`
    const res = await suiteStore.suiteFetch(`/pole-dev/requests/export${params}`, {
      method: 'GET',
      credentials: 'include',
    })
    if (!res || !res.ok) throw new Error("Echec de l'export")
    const blob = await res.blob()
    const filename =
      res.headers.get('Content-Disposition')?.match(/filename="?([^";]+)"?/)?.[1] ?? `pole-dev.${filters.format ?? 'xlsx'}`
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
  }

  return {
    requests,
    total,
    page,
    pageSize,
    request,
    loading,
    fetchAll,
    fetchOne,
    create,
    update,
    addComment,
    changeStatus,
    exportRequests,
    fetchReadyForCommunication,
    sendCommunication,
    pushToGithub,
    syncGithub,
    fetchSprintStats,
  }
})
